/**
 * Pure mapping from site map wire DTOs to domain types.
 *
 * Levels and reasons are narrowed exactly as in the reliability adapter: an unrecognised
 * level is Unknown, unrecognised reasons are dropped, and reasons only accompany Suspect.
 */

import { toReliabilityLevel, toReliabilityReasons } from '../../site';
import type {
  MapSite,
  SiteHealth,
  SiteMapHealth,
  SiteMapHealthEntryResponse,
  SiteMapHealthResponse,
  SiteMapSiteResponse,
  SiteMapSites,
  SiteMapSitesResponse,
} from './siteMapModels';

function inRange(value: number, limit: number): boolean {
  return Number.isFinite(value) && value >= -limit && value <= limit;
}

/** One bad upstream record must not break the map, so unusable locations are dropped. */
function hasUsableLocation(site: SiteMapSiteResponse): boolean {
  return inRange(site.location.latitudeDeg, 90) && inRange(site.location.longitudeDeg, 180);
}

function toMapSite(site: SiteMapSiteResponse): MapSite {
  return {
    siteId: site.siteId,
    name: site.name,
    location: {
      latitudeDeg: site.location.latitudeDeg,
      longitudeDeg: site.location.longitudeDeg,
    },
    ...(site.timezone != null && { timezone: site.timezone }),
    ...(site.countryCode != null && { countryCode: site.countryCode }),
    fixedBayCount: site.fixedBayCount,
    superBayCount: site.superBayCount,
  };
}

export function toSiteMapSites(response: SiteMapSitesResponse): SiteMapSites {
  return {
    generatedAt: response.generatedAt,
    sites: response.sites.filter(hasUsableLocation).map(toMapSite),
  };
}

function toSiteHealth(entry: SiteMapHealthEntryResponse): SiteHealth {
  const level = toReliabilityLevel(entry.level);
  return {
    siteId: entry.siteId,
    level,
    reasons: level === 'Suspect' ? toReliabilityReasons(entry.reasons) : [],
    ...(entry.statusSnapshotAt != null && { statusSnapshotAt: entry.statusSnapshotAt }),
    ...(entry.lastShotAt != null && { lastShotAt: entry.lastShotAt }),
    recentShots: entry.recentShots,
  };
}

export function toSiteMapHealth(response: SiteMapHealthResponse): SiteMapHealth {
  return {
    evaluatedAt: response.evaluatedAt,
    recentShotsWindowS: response.recentShotsWindowS,
    sites: response.sites.map(toSiteHealth),
  };
}
